import { apiService } from "api/apiService";
import { isArray } from "chart.js/helpers";
import paginationModel from "models/pagination.js";
import defaultRatingIcons from "../store/modules/components/ratingIcons";

export const defaultDebounceTime = parseInt(
  import.meta.env?.VITE_DEBOUNCE_TIME || 1500,
);

const debounce = (callbackFunction, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      callbackFunction(...args);
      timeoutId = null;
    }, delay);
  };
};

export const schoolId = localStorage.getItem("school_id");
const schoolName = localStorage.getItem("school_name");
export const employeeId =
  sessionStorage.getItem("employee_id") || localStorage.getItem("employee_id");
export const userId = JSON.parse(localStorage.getItem("user"))?.id;
const tool = localStorage.getItem("tool");
const currentShortage = localStorage.getItem("employee_shortage");
export const isImpersonating = !!localStorage.getItem(
  "webmaster_refresh_token",
);
export const userName = localStorage.getItem("user_name");
const firstName = localStorage.getItem("first_name");
const user = JSON.parse(localStorage.getItem("user"));

export const checkRole = (role) => {
  if (user) {
    return !!user.role.includes(role);
  }
};

export const createSteppedArray = (min, max, step) => {
  let arr = [];

  for (let i = min; i <= max; i += step) {
    arr.push(i);
  }

  return arr;
};

/**
 * Returns the current school year as a string, based on the current date.
 * The school year is assumed to start in August (month > 7).
 * If `short` is true, years are formatted as two digits (e.g., "23/24").
 * Otherwise, years are formatted as four digits (e.g., "2023/2024").
 *
 * @param {boolean} [short=false] - Whether to use short (two-digit) year format.
 * @returns {string} The current school year in the format "YYYY/YYYY" or "YY/YY".
 */
export const getCurrentSchoolYear = (short = false) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const yearFormat = (year) => (short ? year % 100 : year);

  if (currentMonth > 6) {
    return `${yearFormat(currentYear)}/${yearFormat(currentYear + 1)}`;
  } else {
    return `${yearFormat(currentYear - 1)}/${yearFormat(currentYear)}`;
  }
};

const roleName = (role) => {
  if (role === "teacher") {
    return "docent";
  }
  if (role === "decaan") {
    return "organisator portfolio";
  }
  if (role === "teamleader") {
    return "teamleider";
  }
  if (role === "organizer") {
    return "organisator";
  }
  if (role === "manager") {
    return "applicatiebeheerder";
  }
  if (role === "webmaster") {
    return "webmaster";
  }
  if (role === "student") {
    return "leerling";
  }
};

const isMentor = (shortageCurrentUser, mentorsArray) => {
  let isMentor = false;
  mentorsArray.forEach((mentor) => {
    if (mentor.shortage === shortageCurrentUser) {
      isMentor = true;
    }
  });
  return isMentor;
};

const replaceProtocolWithHttps = (url) => {
  // Check if the URL starts with 'http://' or 'https://'
  if (url.startsWith("http://")) {
    // Replace 'http://' with 'https://'
    return "https://" + url.substring(7);
  } else if (url.startsWith("https://")) {
    // The URL already uses 'https://' protocol, no need to change it
    return url;
  } else {
    // If the URL doesn't start with either 'http://' or 'https://', assume 'https://'
    return "https://" + url;
  }
};

const checkIfStringContainsWords = (string, word) => {
  const lowercaseInput = string.toLowerCase();
  return lowercaseInput.includes(word.toLowerCase());
};

function objectToQueryString(obj) {
  let queryString = "";
  for (const key in obj) {
    if (Array.isArray(obj[key])) {
      // If it's an array, join values with a comma
      if (obj[key].length > 0) {
        queryString += `&${key}=${obj[key].join(",")}`;
      }
    } else if (obj[key] != null && obj[key] !== "") {
      // If it's not an array and has a valid value
      queryString += `&${key}=${obj[key]}`;
    }
  }
  return queryString.slice(1); // Remove the leading '&'
}

const shorten = (text, maxLength = 100) => {
  if (!text) {
    return "";
  }
  if (text.length <= maxLength) {
    return text;
  }
  return text.substr(0, maxLength) + "...";
};

const DEFAULT_MAPPED_TERMS = {
  environment: "Studentbespreking",
  student: "student",
  students: "studenten",
  trafficLight: "studentstoplicht",
  trafficLights: "studentstoplichten",
  mentor: "coach",
  mentors: "coaches",
  mentorklas: "klas",
  mentorklassen: "klassen",
  mentorleerlingen: "studenten",
  mentorleerling: "student",
  mentorgroep: "coachgroep",
};

const STUDENTMEETING_MAPPED_TERMS = {
  environment: "Leerlingbespreking",
  student: "leerling",
  students: "leerlingen",
  trafficLight: "leerlingstoplicht",
  trafficLights: "leerlingstoplichten",
  mentor: "mentor",
  mentors: "mentoren",
  mentorklas: "mentorklas",
  mentorklassen: "mentorklassen",
  mentorleerlingen: "mentorleerlingen",
  mentorleerling: "mentorleerling",
  mentorgroep: "mentorgroep",
};

export const mappedTerms =
  tool !== "leerlingbespreking"
    ? DEFAULT_MAPPED_TERMS
    : STUDENTMEETING_MAPPED_TERMS;

const mapStatus = (status) => {
  switch (status) {
    case "open":
      return "Open";
    case "open_for_students":
      return `Open voor ${mappedTerms.students}`;
    case "closed_modify":
      return "Gesloten, aanpassingen nog mogelijk";
    case "closed_no_modify":
      return "Gesloten, aanpassingen niet meer mogelijk";
    case "archived":
      return "Afgerond, aan de slag!";
    case "concept":
      return "Ingepland";
  }
};

export const mapMeetingType = (type) => {
  switch (type) {
    case "meeting":
    case "meetings":
    case "student_meeting":
    case "student_meetings":
      return "Leerlingbespreking";
    case "transition_meeting":
    case "transition_meetings":
      return "Adviesbespreking";
    case "group_meeting":
    case "group_meetings":
      return "Groepsbespreking";
  }
};

export const generateMenuTitle = (route, type = "title") => {
  let title;

  // Check if route has meta title
  if (route?.meta[type]) {
    title = route.meta[type];

    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      const { role: userRoles } = user;

      if (typeof route.meta[type] === "object" && route.meta[type] !== null) {
        title = route.meta[type].general;

        for (const role of userRoles) {
          if (route.meta[type]?.[role]) {
            title = route.meta[type][role];

            // For now break on the first occurance
            break;
          }
        }
      }
    }
  }

  return title;
};

export const isStudent = () => {
  return !!user.role.includes("student");
};

function base64ToBlob(base64, type = "application/octet-stream") {
  const binStr = atob(base64);
  const len = binStr.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    arr[i] = binStr.charCodeAt(i);
  }
  return new Blob([arr], { type: type });
}

const printItem = async (url, formData = null) => {
  let win = window.open("");
  win.document.write(
    "<html><head><title>Bijlage</title></head><body style='margin:0;padding:0;font-family: Helvetica'><div style='width:100%;background: #1d2344;height: 42px;'></div><div style='text-align:center;margin-top:20px'>Het PDF bestand wordt geladen, dit kan 10 tot 60 seconden duren...<br><img style='width:50px; margin-top:25px' src='images/loading-gif.gif'></div><div style='width:100%;background: #1d2344;height: 42px; position:absolute;bottom:0'></div></body></html>",
  );

  return new Promise((resolve, reject) => {
    apiService
      .post(url, formData)
      .then((res) => {
        const pos = res.data.lastIndexOf('"');
        let data = res.data.slice(pos + 1);
        data = data.replace(/^(?=\n)$|^\s*|\s*$|\n\n+/gm, "");

        const blob = base64ToBlob(data, "application/pdf");
        win.document.body.innerHTML = "";
        win.document.write(
          '<iframe src="' +
            URL.createObjectURL(blob) +
            '" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>',
        );

        resolve();
      })
      .catch((e) => {
        win.document.body.innerHTML = "";
        win.document.write(
          "<html><head><title>Bijlage</title></head><body style='margin:0;padding:0;font-family: Helvetica'><div style='width:100%;background: #1d2344;height: 42px;'></div><div style='text-align:center;margin-top:20px'>Er is iets misgegaan, sluit dit venster en probeer het later opnieuw.</div><div style='width:100%;background: #1d2344;height: 42px; position:absolute;bottom:0'></div></body></html>",
        );
        reject(e);
      });
  });
};

const newPdfTabV2 = (res) => {
  const pos = res.lastIndexOf('"');
  let data = res.slice(pos + 1);
  data = data.replace(/^(?=\n)$|^\s*|\s*$|\n\n+/gm, "");

  const win = window.open();
  const blob = base64ToBlob(data, "application/pdf");
  win.document.write(
    '<iframe src="' +
      URL.createObjectURL(blob) +
      '" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>',
  );

  function base64ToBlob(base64, type = "application/octet-stream") {
    const binStr = atob(base64);
    const len = binStr.length;
    const arr = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      arr[i] = binStr.charCodeAt(i);
    }
    return new Blob([arr], { type: type });
  }
};

const newTab = (data) => {
  window.open(data);
};

const getSuggestions = async (type) => {
  let suggestions = null;
  if (isArray(type)) {
    suggestions = await apiService.get(
      `/api/suggestions?types=${type.join(",")}`,
    );
  } else {
    suggestions = await apiService.get(`/api/suggestions?type=${type}`);
  }

  return suggestions.data;
};

const percentageValuesToObject = (percentage, score, max_score) => {
  if (isNaN(percentage)) {
    return {
      value: 0,
      completed: 0,
      total: 0,
      label: "0%",
    };
  }
  return {
    value: percentage / 100,
    completed: score,
    total: max_score,
    label: parseFloat(percentage).toFixed(0) + "%",
  };
};

const progressValuesToObject = (score, max_score) => {
  let checkIsNaN =
    isNaN(score / max_score) || (score > max_score && max_score === 0);

  return checkIsNaN
    ? {
        value: 0,
        completed: 0,
        total: 0,
        label: "0%",
      }
    : {
        value: score / max_score,
        completed: score,
        total: max_score,
        label: `${((score / max_score) * 100).toFixed(0)}%`,
      };
};

const ratingIcons = defaultRatingIcons.state().icons;

/**
 * This will create a component name from a file path
 * @param {string} filePath
 * @returns {string | null} The component name or null if no match is found
 */
export const extractComponentName = (filePath) => {
  // Use a regular expression to match the component name
  const match = filePath.match(/\/([^/]+)\.vue$/);

  // Check if a match is found
  if (match && match[1]) {
    // Return the captured component name
    return match[1];
  }

  // Return null if no match is found
  return null;
};

export const fileMimeTypeExtensions = {
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.oasis.opendocument.text": "odt",
  "application/x-iwork-pages-sffpages": "pages",
  "application/x-iwork-keynote-sffkey": "key",
  "application/x-iwork-numbers-sffnumbers": "numbers",
  "application/zip-pages": "zip-pages",
  "application/zip-numbers": "zip-numbers",
  "application/zip-key": "zip-key",
};

export const imageMimeTypeExtensions = {
  "image/jpg": "jpg",
  "image/png": "png",
  "image/heic": "heic",
  "image/jpeg": "jpeg",
};

export const videoMimeTypeExtensions = {
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/x-matroska": "mkv",
  "video/webm": "webm",
  "video/x-msvideo": "avi",
  "video/x-ms-wmv": "wmv",
};

const allExtensionToMimeTypes = {
  ...fileMimeTypeExtensions,
  ...videoMimeTypeExtensions,
};

const fileTypesToMimeTypes = (array) => {
  return array
    .map((ext) => {
      // Find the key (MIME type) whose value matches the extension
      const mime = Object.keys(allExtensionToMimeTypes).find(
        (key) => allExtensionToMimeTypes[key] === ext,
      );
      return mime || null;
    })
    .filter((mime) => mime !== null);
};

// Allowed file types for attachments
const mimeTypeIncludingExtension = {
  ...fileMimeTypeExtensions,
  ...imageMimeTypeExtensions,
  ...videoMimeTypeExtensions,
};

export const allowedFileTypes = () => {
  return Object.keys(mimeTypeIncludingExtension);
};

export const getExtensionFromMimeType = (mimeType) => {
  return mimeTypeIncludingExtension[mimeType] || null;
};

const appleFileTypes = [
  "application/x-iwork-pages-sffpages",
  "application/x-iwork-keynote-sffkey",
  "application/x-iwork-numbers-sffnumbers",
];

const allowedFileExtensions = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".doc",
  ".docx",
  ".ppt",
  ".pptx",
  ".xls",
  ".xlsx",
  ".odt",
  ".png",
  ".pages",
  ".key",
  ".numbers",
];

const mapRatingIconToName = (value) => {
  let name = "";
  ratingIcons.forEach((icon) => {
    if (icon.id === value) {
      name = icon.name;
    }
  });

  return name;
};

export let invalidFileMessage =
  "Dit bestand is niet toegestaan. Toegestane bestanden: " +
  allowedFileExtensions.join(", ");

export const userHasRole = (roles) => {
  if (Array.isArray(roles) && roles.length === 0) {
    return true;
  }

  const { role: userRoles = [] } = JSON.parse(
    localStorage.getItem("user") || "{}",
  );

  return userRoles.some((role) => roles.includes(role));
};

const activeRoles = () => {
  const { role: userRoles = [] } = JSON.parse(
    localStorage.getItem("user") || "{}",
  );

  return userRoles;
};

const setPaginationMeta = (meta) => {
  // Create a map to change the keys from the backend
  // We receive current_page but we have to send it as page
  const keyMap = {
    current_page: "page",
  };

  return Object.fromEntries(
    Object.entries(meta)
      .map(([key, value]) => {
        // Map the keys to our set keys
        const newKey = keyMap[key] || key;

        return [newKey, value];
      })
      // Add only keys that are part of the default model
      .filter(([key]) => Object.keys(paginationModel).includes(key)),
  );
};

export const isDevelopmentEnvironment =
  import.meta.env.VITE_APP_ENV === "development";

export const isStagingEnvironment = import.meta.env.VITE_APP_ENV === "staging";

export const rewriteModelToFormData = (model) => {
  const formData = new FormData();

  const processAttachment = (attachment, index) => {
    if (attachment instanceof File) {
      formData.append(`attachments[${index}]`, attachment);
    }
  };

  const appendFormData = (prefix, obj) => {
    // If the value is a File, append it directly
    if (obj instanceof File) {
      formData.append(prefix, obj);
      return;
    }

    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        appendFormData(`${prefix}[${index}]`, item);
      });
    } else if (typeof obj === "object" && obj !== null) {
      Object.entries(obj).forEach(([subKey, subValue]) => {
        appendFormData(`${prefix}[${subKey}]`, subValue);
      });
    } else {
      if (typeof obj === "boolean") {
        obj = obj ? 1 : 0;
      }

      formData.append(prefix, obj);
    }
  };

  Object.entries(model).forEach(([key, value]) => {
    if (key === "attachments" && Array.isArray(value) && value.length) {
      value.forEach(processAttachment);
    } else if (key === "banner_image") {
      // Only send banner_image when it is a File (new/changed). Skip existing objects/metadata.
      if (value instanceof File) {
        appendFormData(key, value);
      }
      // If not a File (e.g., existing banner object or null), do not append.
    } else {
      appendFormData(key, value);
    }
  });

  return formData;
};

/**
 * Retrieves a cookie value by name from document.cookie.
 */
export const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));

  return match ? decodeURIComponent(match[2]) : null;
};

export const defaultOpened = (dataset, meetingId, oneNotArchived = false) => {
  const foundId = dataset.find(({ id }) => {
    const parsedId = parseInt(meetingId);
    return parsedId === id;
  })?.id;

  // Is there is a parameter meetingId and it matches an id in the dataset, return that id to open it.
  if (foundId) {
    return foundId;
  } else {
    // According to PO if there is more than 1 meeting close all. If there is only 1 meeting, open that one.
    // For a student open if there's just one meeting with the status "archived" (open for students) then open that one.
    if (oneNotArchived) {
      return dataset.find(({ status }) => status !== "archived")?.id;
    } else {
      return dataset.length > 1 ? null : dataset[0].id;
    }
  }
};

// Converts snake_case to camelCase
export const toCamelCase = (str) =>
  str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

// Recursively convert keys in an object to camelCase
export const camelizeKeys = (obj) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [toCamelCase(key), value]),
  );

export const fakeRequest = () => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const isSuccess = Math.random() > 0.5;
      if (isSuccess) {
        resolve({ message: "Success!", data: {} });
      } else {
        reject(new Error("Request failed!"));
      }
    }, 1000);
  });
};

export const setTargetBlank = (node) => {
  if (node.nodeType === 1) {
    const links = node.matches("a") ? [node] : node.querySelectorAll("a");

    links.forEach((link) => {
      link.setAttribute("target", "_blank");
    });
  }
};

// Parse with the browser instead of a tag regex: a regex such as /<[^>]+>/
// can be defeated by nested tags (`<scr<b></b>ipt>`), and the parser also
// decodes entities (&amp;, &nbsp;, …). Every tag boundary becomes
// `tagReplacement` so words in adjacent elements stay separated.
const htmlToText = (html, tagReplacement) => {
  const { body } = new DOMParser().parseFromString(html, "text/html");

  if (tagReplacement !== "") {
    body.querySelectorAll("*").forEach((element) => {
      element.before(tagReplacement);
      element.after(tagReplacement);
    });
  }

  return body.textContent ?? "";
};

// Replace tags with a space, convert &nbsp;/NBSP to spaces, then collapse multiple spaces
export const stripHTMLTags = (val, { preserveLineBreaks = false } = {}) => {
  if (!val) {
    return "";
  }

  if (preserveLineBreaks) {
    return htmlToText(
      val
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
        .replace(/<\/(div|h[1-6]|li)>/gi, "\n"),
      "",
    )
      .replace(/\u00A0/g, " ")
      .replace(/[^\S\n]+/g, " ")
      .replace(/\n /g, "\n")
      .trim();
  }

  return htmlToText(val, " ")
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const trueFalseOptions = [
  { value: true, label: "Ja" },
  { value: false, label: "Nee" },
];

export const AUTH_STORAGE_KEYS = [
  "user",
  "user_token",
  "refresh_token",
  "expires_in",
  "employee_id",
  "school_id",
  "school_name",
  "user_name",
  "impersonated_user_type",
  "is_refreshing",
  "validate_2fa",
  "first_name",
  "tool",
  "school_hash",
  "school_settings",
  "show_nps",
];

export const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
};

function checkOverlapAndHide(overlapTarget, hideTarget) {
  const overlapElement =
    typeof overlapTarget === "string"
      ? document.querySelector(overlapTarget)
      : overlapTarget;

  const hideElement =
    typeof hideTarget === "string"
      ? document.querySelector(hideTarget)
      : hideTarget;

  if (!overlapElement || !hideElement) {
    return;
  }

  const overlapRect = overlapElement.getBoundingClientRect();
  const hideRect = hideElement.getBoundingClientRect();

  const isOverlapping = !(
    overlapRect.right < hideRect.left ||
    overlapRect.left > hideRect.right ||
    overlapRect.bottom < hideRect.top ||
    overlapRect.top > hideRect.bottom
  );

  hideElement.style.opacity = isOverlapping ? "0" : "1";
  hideElement.style.pointerEvents = isOverlapping ? "none" : "auto";
}

//Export the functions
const useHelpers = () => ({
  defaultDebounceTime,
  debounce,
  mappedTerms,
  schoolId,
  schoolName,
  employeeId,
  userId,
  tool,
  currentShortage,
  isImpersonating,
  userName,
  user,
  getSuggestions,
  ratingIcons,
  firstName,
  studentId: localStorage.getItem("student_id"),
  mapStatus,
  isStudent,
  checkRole,
  roleName,
  shorten,
  newPdfTabV2,
  newTab,
  objectToQueryString,
  checkIfStringContainsWords,
  replaceProtocolWithHttps,
  printItem,
  isMentor,
  allowedFileTypes,
  appleFileTypes,
  invalidFileMessage,
  mapRatingIconToName,
  allowedFileExtensions,
  mapMeetingType,
  userHasRole,
  generateMenuTitle,
  activeRoles,
  percentageValuesToObject,
  progressValuesToObject,
  setPaginationMeta,
  rewriteModelToFormData,
  imageMimeTypeExtensions,
  videoMimeTypeExtensions,
  getCookie,
  defaultOpened,
  toCamelCase,
  camelizeKeys,
  fakeRequest,
  stripHTMLTags,
  trueFalseOptions,
  fileTypesToMimeTypes,
  clearAuthStorage,
  isDevelopmentEnvironment,
  checkOverlapAndHide,
});

export default useHelpers;
